/**
 * What one of the sixteen routes renders until the task that owns it lands.
 *
 * It is deliberately **not** an `EmptyState`. "No data" is a fact about a company's
 * records; "this screen has not been built" is a fact about this build, and
 * `PRODUCT.md` §4 exists because collapsing two different facts into one sentence is
 * how a person ends up acting on the wrong one. So the placeholder names the task
 * that owns the screen and says what is already in place.
 *
 * It also names its own permission requirement. Getting here means the route guard
 * agreed the session holds one of them, and having it on screen means the first
 * reviewer of task 42–48 can see what the frame already decided.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import type { MenuItem } from '../nav/menu';
import { ScreenHeading } from '../shell/ScreenHeading';
import { StatusNote } from '../states/StatusNote';

export function PlaceholderScreen({ item }: { readonly item: MenuItem }): ReactNode {
  const t = useT();
  const label = t(item.labelKey);
  return (
    <div className="wp-placeholder">
      <ScreenHeading>{label}</ScreenHeading>
      <StatusNote
        severity="info"
        title={t('placeholder.title', { screen: label })}
        testId={`placeholder-${item.id}`}
      >
        <p>{t('placeholder.body', { screen: label, task: item.task })}</p>
        <p className="wp-small wp-muted">
          <code>{item.requires.join(' | ')}</code>
        </p>
      </StatusNote>
    </div>
  );
}
